import type { VesselDef } from '../types/game';

const VESSEL_BASE_COST = 250;

export const VESSEL_DEFS: VesselDef[] = [
  {
    id: 'rowboat',
    name: 'Rowboat',
    description: 'A humble wooden boat. Short trips, modest returns.',
    cost: Math.floor(VESSEL_BASE_COST * Math.pow(1, 4)),
    coinsPerSecond: 1,
    tripDurationSeconds: 30,
  },
  {
    id: 'trawler',
    name: 'Trawler',
    description: 'A proper fishing vessel with nets. Longer trips, better pay.',
    cost: Math.floor(VESSEL_BASE_COST * Math.pow(2, 4)),
    coinsPerSecond: 5,
    tripDurationSeconds: 45,
  },
  {
    id: 'factory_ship',
    name: 'Factory Ship',
    description: 'Processes fish on board. Industrial scale operations.',
    cost: Math.floor(VESSEL_BASE_COST * Math.pow(3, 4)),
    coinsPerSecond: 25,
    tripDurationSeconds: 60,
  },
  {
    id: 'drone_fleet',
    name: 'Drone Fleet',
    description: 'Autonomous drones scout and catch fish in deep waters.',
    cost: Math.floor(VESSEL_BASE_COST * Math.pow(4, 4)),
    coinsPerSecond: 120,
    tripDurationSeconds: 90,
  },
  {
    id: 'ai_armada',
    name: 'AI Armada',
    description: 'A self-managing fleet run by artificial intelligence. Maximum profits.',
    cost: Math.floor(VESSEL_BASE_COST * Math.pow(5, 4)),
    coinsPerSecond: 600,
    tripDurationSeconds: 150,
  },
];

// ── Boat name generator ──

const BOAT_ADJECTIVES = [
  'Salty', 'Iron', 'Wave', 'Sea', 'Storm', 'Coral', 'Tide', 'Misty',
  'Brave', 'Swift', 'Golden', 'Silver', 'Rusty', 'Lucky', 'Deep', 'Wild',
  'Silent', 'Aurora', 'Crimson', 'Jade', 'Neptune', 'Thunder', 'Starlight',
];

const BOAT_NOUNS = [
  'Dancer', 'Chaser', 'Hunter', 'Rider', 'Runner', 'Seeker', 'Wanderer',
  'Mist', 'Spray', 'Breeze', 'Gale', 'Current', 'Hawk', 'Dolphin',
  'Compass', 'Horizon', 'Voyager', 'Harpoon', 'Whale', 'Marlin', 'Mermaid',
  'Drift', 'Pearl', 'Star', 'Osprey', 'Cutter', 'Clipper',
];

export function generateBoatName(): string {
  const adj = BOAT_ADJECTIVES[Math.floor(Math.random() * BOAT_ADJECTIVES.length)];
  const noun = BOAT_NOUNS[Math.floor(Math.random() * BOAT_NOUNS.length)];
  return `${adj} ${noun}`;
}

export function getVesselDefById(id: string): VesselDef | undefined {
  return VESSEL_DEFS.find(v => v.id === id);
}

export function getVesselCap(businessLevel: number, dockTier: number): number {
  return 2 + Math.floor(businessLevel / 10) + dockTier;
}

// ── Bell curve profit ──

function randomNormal(): number {
  let u = 0, v = 0;
  while (u === 0) u = Math.random();
  while (v === 0) v = Math.random();
  return Math.sqrt(-2.0 * Math.log(u)) * Math.cos(2.0 * Math.PI * v);
}

export function getVesselTripProfit(vesselId: string, businessLevel: number): number {
  const def = getVesselDefById(vesselId);
  if (!def) return 0;
  const businessBonus = 1 + 0.07 * Math.pow(Math.max(0, businessLevel - 1), 0.6);
  const mean = def.coinsPerSecond * def.tripDurationSeconds * businessBonus;
  const multiplier = 1 + randomNormal() * 0.25;
  const raw = mean * multiplier;
  const clamped = Math.max(mean * 0.3, Math.min(mean * 2.0, raw));
  return Math.round(clamped * 100) / 100;
}
